import { router, useLocalSearchParams } from 'expo-router';
import React, { useState } from 'react';
import { type SceneRendererProps, TabView } from 'react-native-tab-view';
import {
    BodyMediumRegular,
    BodySmallRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    CalendarSync,
    FullScreenSpinner,
    H2Emphasized,
    Icon,
    InfoRow,
    RowList,
    ScreenContainer,
    useToastController,
    View,
    XStack,
    YStack,
} from 'smoothie';

import { getLastFourAccountNumber } from '../../../common/utils/bankingUtils';
import { getNonTerminatedCards } from '../../../common/utils/cardUtils';
import LogRocketFacade from '../../../logger/log-rocket/LogRocketFacade';
import { captureException } from '../../../logger/Logger';
import useGetAccountDisplayDetailsFromGlobalState from '../../hooks/useGetAccountDisplayDetailsFromGlobalState';
import useGetAllBankAccountDetails from '../../hooks/useGetAllBankAccountDetails';
import useUpdateAutoPay from '../../hooks/useUpdateAutoPay';
import { getBankingDisclosures } from '../Disclosures';

enum EnableAutoPayStep {
    Overview = 'overview',
    Confirmation = 'confirmation',
}

const EnableAutoPay = () => {
    LogRocketFacade.tagPage('EnableAutoPay');

    const { syncteraAccountId } = useLocalSearchParams<{
        syncteraAccountId: string;
    }>();

    const toast = useToastController();
    const [index, setIndex] = useState(0);
    const routes = Object.values(EnableAutoPayStep).map((step) => ({
        key: step,
    }));

    const getRouteIndex = (step: EnableAutoPayStep) =>
        routes.findIndex(({ key }) => key === step);

    const confirmationIndex = getRouteIndex(EnableAutoPayStep.Confirmation);

    const { mutateAsync: updateAutoPay, isPending: isUpdateAutoPayPending } =
        useUpdateAutoPay();

    const { refetchAccountDetails, isLoading: isLoadingAccountDetails } =
        useGetAllBankAccountDetails(false);

    const { account } = useGetAccountDisplayDetailsFromGlobalState({
        syncteraAccountId,
    });

    if (account === null || isUpdateAutoPayPending || isLoadingAccountDetails) {
        return <FullScreenSpinner />;
    }

    const cards = getNonTerminatedCards(account.cards)
        .map((card) => `*${card.lastFour}`)
        ?.join(', ');

    const handleUpdateAutoPay = async () => {
        try {
            await updateAutoPay({
                urlParams: {
                    syncteraAccountId:
                        account.chargeSecuredSyncteraAccountId as string,
                },
                body: {
                    autoPayEnabled: true,
                    disclosures: getBankingDisclosures({
                        enableAutoPay: true,
                        policyTypes: [],
                    }),
                },
            });
            void refetchAccountDetails();
            setIndex(confirmationIndex);
        } catch (e) {
            captureException(e);
            toast.show('Something went wrong');
        }
    };

    const onPressBack = () => {
        if (router.canGoBack()) {
            router.back();
        } else {
            router.navigate('/home/banking');
        }
    };

    const renderScene = ({
        route,
    }: SceneRendererProps & { route: { key: EnableAutoPayStep } }) => {
        if (routes[index]?.key !== route.key) {
            return null;
        }

        const getStepContent = () => {
            switch (route.key) {
                case EnableAutoPayStep.Overview:
                    return (
                        <AutoPayOverviewTab
                            card={`${account.nickname} Card (${cards})`}
                            from={`Reserved funds in ${account.nickname} (*${getLastFourAccountNumber(account.accountNumber)})`}
                            onPress={handleUpdateAutoPay}
                        />
                    );
                case EnableAutoPayStep.Confirmation:
                    return (
                        <AutoPayConfirmationTab
                            onPress={() => {
                                router.navigate({
                                    pathname: '/banking/card/manage-card',
                                    params: { syncteraAccountId },
                                });
                            }}
                        />
                    );
                default:
                    return <></>;
            }
        };

        return (
            <ScreenContainer
                hasBackButton={index !== confirmationIndex}
                onBack={onPressBack}
                hasCloseButton={index === confirmationIndex}
                onClose={() => {
                    router.navigate({
                        pathname: '/banking/card/manage-card',
                        params: { syncteraAccountId },
                    });
                }}
                onFallbackRoute="/home/banking"
            >
                {getStepContent()}
            </ScreenContainer>
        );
    };

    return (
        <View height="100%">
            <TabView
                swipeEnabled={false}
                navigationState={{ index, routes }}
                renderScene={renderScene}
                renderTabBar={() => <></>}
                onIndexChange={setIndex}
            />
        </View>
    );
};

interface AutoPayOverviewTabProps {
    card: string;
    from: string;
    onPress: () => void;
}

export const AutoPayOverviewTab = ({
    card,
    from,
    onPress,
}: AutoPayOverviewTabProps) => {
    return (
        <YStack
            height="100%"
            justifyContent="space-between"
            alignItems="center"
        >
            <View gap={20} alignItems="center">
                <View
                    alignSelf="center"
                    padding={20}
                    borderRadius={20}
                    backgroundColor="$green900"
                    marginTop={20}
                >
                    <Icon icon={CalendarSync} color="$white" size={40} />
                </View>
                <H2Emphasized>Turn AutoPay on</H2Emphasized>
                <BodyMediumRegular color="$gray1000" textAlign="center">
                    Automatically pay your statement balance on the 22nd of each
                    month using reserved funds in your Fruitful Cash Account set
                    aside as you spend.
                </BodyMediumRegular>

                <YStack gap={12} marginTop={12}>
                    <RowList>
                        <InfoRow title="Card" value={card} />
                        <InfoRow title="From" value={from} />
                        <InfoRow
                            title="Amount"
                            value="Last statement balance"
                        />
                        <InfoRow title="Date" value="The 22nd of each month" />
                    </RowList>

                    <BodySmallRegular
                        color="$gray1000"
                        textAlign="center"
                        paddingBottom={12}
                    >
                        By confirming below, I authorize Fruitful to use
                        Reserved funds as payment on my card balance as
                        indicated above.
                    </BodySmallRegular>
                </YStack>
            </View>

            <XStack width="100%">
                <Button
                    variant={ButtonVariant.PRIMARY}
                    text="Turn AutoPay on"
                    size={ButtonSize.LARGE}
                    onPress={onPress}
                />
            </XStack>
        </YStack>
    );
};

const AutoPayConfirmationTab = ({ onPress }: { onPress: () => void }) => {
    return (
        <YStack
            height="100%"
            justifyContent="space-between"
            alignItems="center"
        >
            <View gap={40} alignItems="center">
                <View
                    alignSelf="center"
                    padding={20}
                    borderRadius={20}
                    backgroundColor="$green900"
                    marginTop={30}
                >
                    <Icon icon={CalendarSync} color="$white" size={40} />
                </View>
                <H2Emphasized>AutoPay is now on!</H2Emphasized>
            </View>
            <Button
                variant={ButtonVariant.PRIMARY}
                text="Done"
                size={ButtonSize.LARGE}
                onPress={onPress}
            />
        </YStack>
    );
};

export default EnableAutoPay;
